import { makeAutoObservable } from "mobx";
import type { SortingState } from "@tanstack/react-table";
import {
  isSortingState,
  readTablePref,
  writeTablePref,
  type TablePrefsKey,
} from "@/components/data-table/table-prefs-storage";
import { tablePrefsEnabledNow } from "@/components/data-table/table-prefs-gate";
import type { SnapshotSort } from "@/queries/reports";
import type { RootStore } from "./root.store";

/** Storage id for the Report View's snapshot table — must stay in step with the
 *  `tableId` that view hands `<DataTable>` (`report-view-page.tsx`), which is
 *  what the column prefs are already filed under. */
const SNAPSHOT_TABLE_ID = "report-snapshot";

/**
 * UI state for the Reports feature (Insights → Reports): the report builder's
 * open state, and each report's snapshot sort. The reports themselves, their
 * runs and their rows live in the query cache (`queries/reports.ts`, NG-2811).
 */
export class ReportStore {
  rootStore: RootStore;

  /**
   * Whether the report builder modal is open. Owned here (not in route-local
   * state) because the builder is launched from multiple surfaces — the index's
   * "Create report" button (issue 1674), the row "Edit" action (issue 1675) and
   * the Report View's Edit entries.
   */
  builderOpen = false;

  /**
   * The id of the report the builder is editing, or `null` for a create flow.
   * Set by `openBuilder(reportId)` so the builder knows to read and pre-populate
   * an existing report.
   */
  builderReportId: string | null = null;

  /**
   * The step the builder should open on (0-based). Defaults to the first step
   * (Report type) for Create and the row "Edit" action; the Report View's
   * "Edit filters" entry point opens directly on the dedicated Filters step
   * (index 2, #3185) by passing `{ step: 2 }` to `openBuilder`. Reset on close.
   */
  builderInitialStep = 0;

  /**
   * Whether the builder was opened from the Report View detail page (its Edit /
   * Edit-filters entries) rather than the index row "Edit" or Create. When true
   * the Name & save step offers a single "Save & view" confirm that also kicks
   * off a fresh run, so the user returns to the detail page seeing their edits
   * reflected. Reset on close.
   */
  builderFromDetail = false;

  /** Active server-side sort for the snapshot, per report (absent = run default). */
  snapshotSortByReport = new Map<string, SnapshotSort>();

  /**
   * The workspace slug each hydrated report's stored sort belongs to, and so the
   * only key `persistSnapshotSort` may write for that report.
   *
   * Deliberately NOT `rootStore.router.workspaceSlug`, nor the slug a caller is
   * handed: the Report View reads that slug off the router, whose params are
   * set from an effect, so it is `""` before that effect runs and still the
   * OUTGOING workspace during a switch. The slug hydration is called with comes
   * from the detail route's `clientLoader` params, which cannot lag.
   *
   * Not observable: bookkeeping about a read, nothing renders it.
   */
  private snapshotSortSlugByReport = new Map<string, string>();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    // `as never`: the overrides type only admits public keys, and the slug map
    // above is bookkeeping rather than state.
    makeAutoObservable(this, { rootStore: false, snapshotSortSlugByReport: false } as never);
  }

  /**
   * Open the report builder. Pass a `reportId` to open it in edit mode for that
   * report (the row "Edit" action, issue 1675); omit it for the create flow.
   * `opts.step` opens the builder on a specific step (0-based) — the Report
   * View's "Edit filters" entry passes `{ step: 2 }` to land on the dedicated
   * Filters step (#3185); Create and the row "Edit" default to the first step.
   * `opts.fromDetail` marks an edit launched from the Report View so save offers
   * a single "Save & view" that re-runs (issue 1985).
   */
  openBuilder(reportId?: string, opts?: { step?: number; fromDetail?: boolean }): void {
    this.builderOpen = true;
    this.builderReportId = reportId ?? null;
    this.builderInitialStep = opts?.step ?? 0;
    this.builderFromDetail = opts?.fromDetail ?? false;
  }

  closeBuilder(): void {
    this.builderOpen = false;
    this.builderReportId = null;
    this.builderInitialStep = 0;
    this.builderFromDetail = false;
  }

  /** Active sort for a report's snapshot (undefined → the run's default order).
   *
   *  Also what the Report View's CSV export sends (`?sort=`), and the export
   *  endpoints validate it exactly as `snapshot` does. They need no guard of
   *  their own: the Export button is disabled until a snapshot page has loaded
   *  successfully, and the snapshot read has by then dropped any sort the run
   *  cannot serve — so an export can only ever carry a sort the run just
   *  accepted. */
  snapshotSort(id: string): SnapshotSort | undefined {
    return this.snapshotSortByReport.get(id);
  }

  /** Sort the snapshot by a column. The snapshot read for the new sort follows the key. */
  setSnapshotSort(id: string, sort: string, order: "asc" | "desc"): void {
    this.snapshotSortByReport.set(id, { sort, order });
    this.persistSnapshotSort(id);
  }

  /**
   * Back to the run's default order, in memory AND in storage: a new run
   * re-resolves its columns, and a sort the server refused would otherwise be
   * restored on the next visit and refused again.
   */
  clearSnapshotSort(id: string): void {
    this.snapshotSortByReport.delete(id);
    this.persistSnapshotSort(id);
  }

  // ---- Persisted snapshot sort (NG-517) -----------------------------------

  /**
   * Scoped to `<workspaceSlug>/<reportId>`, not the workspace: a stored sort
   * names a column of ONE report's resolved snapshot, so carrying it to another
   * report would sort by a column that run may not have.
   */
  private snapshotSortKey(slug: string, id: string): TablePrefsKey {
    return { tableId: SNAPSHOT_TABLE_ID, scope: `${slug}/${id}` };
  }

  /**
   * The ONE place a report's snapshot sort is persisted — it derives what to
   * write from the map rather than from its caller, so a mutator only has to say
   * "this report's sort changed".
   *
   * An unhydrated report has no known workspace, so there is no key worth
   * writing — only reachable before the detail route's `clientLoader` has run.
   */
  private persistSnapshotSort(id: string): void {
    // Behind `persisted-table-prefs` (NG-517) — this store reaches
    // `writeTablePref` directly, so the hook-side gate does not cover it.
    if (!tablePrefsEnabledNow(this.rootStore.featureFlags)) return;
    const slug = this.snapshotSortSlugByReport.get(id);
    if (!slug) return;
    const active = this.snapshotSortByReport.get(id);
    writeTablePref(
      this.snapshotSortKey(slug, id),
      "sorting",
      // A cleared sort is stored as `[]`, not left behind: `readTablePref`
      // distinguishes empty from absent, so a stale entry would come back.
      active ? [{ id: active.sort, desc: active.order === "desc" }] : [],
    );
  }

  /**
   * Restore a report's stored snapshot sort, and record the workspace its key
   * belongs to.
   *
   * Called from the detail route's `clientLoader` BEFORE it starts the snapshot
   * read, so that first request already carries the restored sort — the
   * snapshot is sorted server-side, so hydrating any later would mean an
   * unsorted page followed by a sorted one.
   *
   * No hydrated-once guard: the state this writes is keyed by report id exactly
   * as the storage key is, so global state restored under a per-workspace key
   * cannot arise here. Every in-session change writes through, so re-reading on
   * a re-mount lands on the value memory already holds.
   *
   * A stored `[]` — what a re-run leaves behind — restores nothing rather than
   * clearing the current sort, so this is safe to call over a live view.
   */
  hydrateSnapshotSort(slug: string, id: string): void {
    // An empty slug or id would build the scope `/<id>` (or `<slug>/`), a bucket
    // nothing ever reads again.
    if (!slug || !id) return;
    // Behind `persisted-table-prefs` (NG-517) — see `persistSnapshotSort`.
    // Bailing BEFORE the slug is recorded leaves persistence unarmed too, so
    // the two halves of the gate cannot disagree.
    if (!tablePrefsEnabledNow(this.rootStore.featureFlags)) return;
    this.snapshotSortSlugByReport.set(id, slug);
    const stored = readTablePref<SortingState>(
      this.snapshotSortKey(slug, id),
      "sorting",
      [],
      isSortingState,
    );
    const first = stored[0];
    if (!first) return;
    this.snapshotSortByReport.set(id, {
      sort: first.id,
      order: first.desc ? "desc" : "asc",
    });
  }
}
